// TODO: Make single interface for local-storage and in-memory storage

import type { RouteLocation } from 'vue-router';
import { v4 as uuid } from 'uuid';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import type {
  IAnalyticsOptions,
  IConnection,
  IEventData,
  IEventDataV2,
  IURLObject,
  IUTMParams,
  PageView3Params,
  TEffectiveNetworkType,
  TPlatform,
} from './types';
import {
  EEventType,
} from './types';
import CONSTANTS from './constants';
import { CookieUtils, getEventTrackingUrlV2, getTrackingURL, isServer } from './utils';
import InMemoryStorage from './utils/InMemoryStorage';
import QLocalStorage from './utils/localStorage';

let LocalStorage: QLocalStorage;
const IMStorage = new InMemoryStorage();

interface IQAnalyticsClient {
  getFeatureFlags: () => Record<string, any>,
  setStartingReferrer: (reset?: boolean) => void,
  checkStartingReferrer: () => void,
  setSessionId: (reset?: boolean) => void,
  checkSessionId: () => void,
  createEventData: (eventName: string, options: IEventData) => IEventData,
  getDefaultEventData: () => IEventData,
  featureFlagsProcessor: (featureFlags: Record<string, any>) => Record<string, any>,
  logEvent: (eventName: string, options: Record<string, any>) => void,
  logEventV2: (eventName: string, options: Record<string, any>) => void,
  sendThroughBeacon: (data: string) => void,
  sendThroughFetch: (body: any) => void,
  send: (data: any) => void,
  createPageView3Event: (currentUrl: RouteLocation, previousUrl: RouteLocation, pageView3Params: PageView3Params) => any,
  logPageView3Event: (currentUrl: RouteLocation, previousUrl: RouteLocation, pageView3Params: PageView3Params) => void,
}
export class QAnalyticsClient implements IQAnalyticsClient {
  private static instance: QAnalyticsClient | null = null;
  private sessionId: string = '';
  private expName: string;
  private expSlot: string;
  private platform: TPlatform = 'web';
  private startingReferrer: string = '';
  private analyticsUrl: string;
  private localStorageKey: string = 'QuizizzAnalytics';
  static isProd: boolean = true;
  private areDevEventsAllowed = false;

  getFeatureFlags: () => Record<string, any>;

  private constructor({ platform = 'web', areDevEventsAllowed = false, isProd = false }: IAnalyticsOptions) {
    QAnalyticsClient.isProd = isProd;
    LocalStorage = new QLocalStorage(isProd);
    this.platform = platform;
    this.analyticsUrl = getTrackingURL(isProd);
    this.setSessionId();
    this.setStartingReferrer();
    this.expName = CookieUtils.get(CONSTANTS.QUIZIZZ_EXP_NAME_KEY) || '';
    this.expSlot = CookieUtils.get(CONSTANTS.QUIZIZZ_EXP_SLOT_KEY) || '';
    this.areDevEventsAllowed = areDevEventsAllowed;

    this.getFeatureFlags = () => {
      const featureFlagsData: Record<string, any> = {};
      if ('serverData' in window) {
        const flags = (window.serverData as any).featureFlags.features;
        Object.keys(flags).forEach((flag) => {
          featureFlagsData[flag] = flags[flag]?.defaultValue;
        });
      }

      return featureFlagsData;
    };
  }

  public static getInstance(opts: IAnalyticsOptions) {
    if (!QAnalyticsClient.instance) {
      QAnalyticsClient.instance = new QAnalyticsClient(opts);
    }
    return QAnalyticsClient.instance;
  }

  static getQueryParams(path?: string): Record<string, any> {
    const url = new URL(path ?? window.location.href);
    const searchParams: Record<string, any> = {};
    url.searchParams.forEach((val, key) => {
      searchParams[key] = val;
    });
    return searchParams;
  }

  static getUtmParams(path?: string): IUTMParams {
    let query: Record<string, any>;
    if (path) {
      query = QAnalyticsClient.getQueryParams(path);
    } else {
      query = QAnalyticsClient.getQueryParams();
    }

    return {
      source: query.utm_source ?? '',
      medium: query.utm_medium ?? '',
      campaign: query.utm_campaign ?? '',
      term: query.utm_term ?? '',
      content: query.utm_content ?? '',
    };
  }

  setStartingReferrer(reset: boolean = false): void {
    if (isServer()) {
      return;
    }

    let QuizizzAnalyticsData = IMStorage.getItem(this.localStorageKey);

    if (!QuizizzAnalyticsData) {
      QuizizzAnalyticsData = LocalStorage.getItem(this.localStorageKey);
    }

    let startingReferrer = QuizizzAnalyticsData?.startingReferrer;

    if (!startingReferrer || reset) {
      startingReferrer = document.referrer;
      const update = {
        sessionId: this.sessionId,
        startingReferrer,
        eventCount: 0,
        expiry: QuizizzAnalyticsData.expiry ?? Date.now() + (1000 * 60 * 30),
      };
      LocalStorage.setItem(this.localStorageKey, update);
      IMStorage.setItem(this.localStorageKey, update);
    }

    this.startingReferrer = startingReferrer;
  }

  checkStartingReferrer(): void {
    const startingReferrer = this.startingReferrer;
    let metaData = IMStorage.getItem(this.localStorageKey);
    if (!metaData) {
      metaData = LocalStorage.getItem(this.localStorageKey);
    }

    if ((metaData?.startingReferrer !== startingReferrer) || metaData?.expiry <= Date.now()) {
      this.setStartingReferrer(true);
    }
  }

  setSessionId(reset: boolean = false): void {
    let quizizzAnalyticsData = IMStorage.getItem(this.localStorageKey);
    if (!quizizzAnalyticsData) {
      quizizzAnalyticsData = LocalStorage.getItem(this.localStorageKey);
    }

    let sessionId = quizizzAnalyticsData?.sessionId;

    if (!sessionId || reset) {
      sessionId = uuid();
      const update = {
        sessionId,
        eventCount: 0,
        expiry: Date.now() + (1000 * 60 * 30),
      };
      LocalStorage.setItem(this.localStorageKey, update);

      IMStorage.setItem(this.localStorageKey, update);
    }

    this.sessionId = sessionId;
  }

  checkSessionId(): void {
    let metaData = IMStorage.getItem(this.localStorageKey);
    if (metaData) {
      const currentSessionId = this.sessionId;
      if (!currentSessionId || metaData?.expiry <= Date.now()) {
        this.setSessionId(true);
      }
    } else {
      metaData = LocalStorage.getItem(this.localStorageKey);
      const currentSessionId = this.sessionId;
      if (!currentSessionId || metaData?.expiry <= Date.now()) {
        this.setSessionId(true);
      }
    }
  }

  static createError(msg: string, shouldThrow: boolean = false): void {
    if (shouldThrow) {
      throw new Error(`QAnalytics error :: ${msg}`);
    }
    if (!QAnalyticsClient.isProd) {
      // eslint-disable-next-line no-console
      console.error(`QAnalytics error :: ${msg}`);
    }
  }

  createEventData(eventName: string, options: IEventData) {
    const { params } = options;
    const eventData = {
      eventName,
      eventType: options.eventType ? options.eventType : EEventType.unknown,
      params: {
        sessionId: this.sessionId,
        ...params,
      },
      featureFlags: options.featureFlags ?? this.getFeatureFlags(),
      platform: this.platform,
      slot: this.expSlot,
      experiment: this.expName,
      time: Date.now(),
      timezoneOffset: new Date().getTimezoneOffset(),
    };

    return eventData;
  }

  getDefaultEventData(): IEventData {
    const defaultEventData: IEventData = {
      params: {
        webflow_referrer: QAnalyticsClient.getQueryParams().webflow_referrer,
        subPlanId: undefined,
        country: '',
        media: '',
        locale: '',
        page: undefined,
        referrer: '',
        url: '',
        showTrial: false,
        price: 0,
        source: '',
      },
      featureFlags: {},
      platform: this.platform,
      slot: this.expSlot,
      experiment: this.expName,
      time: Date.now(),
      timezoneOffset: new Date().getTimezoneOffset(),
    };
    return defaultEventData;
  }

  featureFlagsProcessor(featureFlags: Record<string, any>): Record<string, any> {
    return cloneDeep(featureFlags);
  }

  logEvent(eventName: string, options: Record<string, any>): void {
    if (!eventName) {
      QAnalyticsClient.createError('No eventName provided in logEvent');
      return;
    }

    const enableFrontendEvent = options.enableFrontendEvent ?? false;

    delete options.enableFrontendEvent;

    this.checkSessionId();
    const defaults = this.getDefaultEventData();
    const eventData = this.createEventData(eventName, {
      ...defaults,
      params: {
        ...defaults.params,
        ...options.params,
        ...options.optionalParams,
        deviceId: CookieUtils.get('quizizz_uid') || null,
        sessionId: CookieUtils.get('quizizz_uid') || this.sessionId,
      },
      featureFlags: options.featureFlags,
    });

    this.send([eventData]);
    if (enableFrontendEvent) {
      this.logEventV2(eventName, options);
    }
    if (eventData.featureFlags['analytics-v2'] === 'ENABLED' && !QAnalyticsClient.isProd) {
      this.logToAnalyticsV2(eventData, 'other');
    }
  }

  logEventV2(eventName: string, options: Record<string, any>): void {
    if (!eventName) {
      QAnalyticsClient.createError('No eventName provided in logEvent');
      return;
    }

    this.checkSessionId();
    const defaults = this.getDefaultEventData();
    const eventType = options.optionalParams.eventType;
    delete options.optionalParams.eventType;
    const eventData = this.createEventData(eventName, {
      ...defaults,
      params: {
        ...defaults.params,
        ...options.params,
        deviceId: CookieUtils.get('quizizz_uid') || null,
      },
      eventType,
      featureFlags: options.featureFlags,
    });

    const currentUrl: IURLObject | null = IMStorage.getItem('pv3CurrentUrl');
    const previousUrl: IURLObject | null = IMStorage.getItem('pv3PreviousUrl');

    const networkInformation: IConnection = (navigator as any).connection;
    const effectiveNetworkType: TEffectiveNetworkType = networkInformation?.effectiveType || 'unknown';

    const url: string = window.location.href;
    const width: number = window.screen.width;
    const height: number = window.screen.height;
    const density: number = window.devicePixelRatio;

    if (!eventData.eventName.startsWith('frontend_')) {
      eventData.eventName = `frontend_${eventData.eventName}`;
    }

    const frontendEventData: IEventDataV2 = {
      eventName: eventData.eventName,
      eventType: eventData.eventType ? eventData.eventType : EEventType.unknown,
      page: {
        url,
        path: currentUrl?.matched ? currentUrl?.matched[currentUrl?.matched.length - 1]?.path : null,
        name: currentUrl?.name || 'UnknownPage',
        params: currentUrl?.query && !isEmpty(currentUrl.query) ? currentUrl.query : null,
      },
      referrer: {
        url: previousUrl?.fullPath || null,
        path: previousUrl?.fullPath || null,
        name: previousUrl?.name || 'UnknownPage',
      },
      deviceId: CookieUtils.get('quizizz_uid') || 'localhost',
      deviceTime: Date.now(),
      timezoneOffset: new Date().getTimezoneOffset(),
      sessionId: this.sessionId,
      country: eventData?.params?.country || null,
      userId: eventData?.params?.userId || null,
      subPlanId: eventData?.params?.subPlanId || null,
      locale: eventData?.params?.locale,
      platform: this.platform,
      screen: {
        width,
        height,
        density,
      },
      featureFlags: this.featureFlagsProcessor(eventData?.featureFlags),
      effectiveNetworkType,
      params: isEmpty(options.optionalParams) ? null : options.optionalParams,
    };

    this.send([frontendEventData]);
    if (eventData.featureFlags['analytics-v2'] === 'ENABLED') {
      this.logToAnalyticsV2(frontendEventData, 'frontend');
    }
  }

  sendThroughBeacon(data: string): void {
    const beacon = navigator.sendBeacon.bind(navigator);
    const url = this.analyticsUrl;
    if (!beacon(url, data)) {
      QAnalyticsClient.createError(`navigator.sendBeacon could not queue the request with data \n ${JSON.parse(data)}`);
    }
  }

  sendThroughFetch(body: any): void {
    const url = this.analyticsUrl;
    fetch(url, {
      method: 'POST',
      cache: 'no-cache',
      mode: 'no-cors',
      headers: {
        'Content-Type': 'application/json',
      },
      body,
      keepalive: false,
    }).catch((e) => {
      if (!QAnalyticsClient.isProd) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    });
  }

  send(data: any): void {
    if (!QAnalyticsClient.isProd) {
      // eslint-disable-next-line no-console
      data?.forEach((ev: any) => console.log('\n EventLoggerService logEvent:', ev?.eventName, ev, '\n\n'));

      if (!this.areDevEventsAllowed) {
        return;
      }
    }

    const body = JSON.stringify(data);
    if (navigator && typeof navigator.sendBeacon !== 'undefined') {
      this.sendThroughBeacon(body);
    } else {
      this.sendThroughFetch(data);
    }
  }

  createPageView3Event(currentUrl: RouteLocation, previousUrl: RouteLocation, pageView3Params: PageView3Params) {
    // check sessionId and reset if inactive for 30mins
    this.checkSessionId();
    this.getFeatureFlags();

    let urlStruct = currentUrl.meta.struct;
    let usedPredefinedStruct = true;

    if (!urlStruct) {
      urlStruct = currentUrl.matched.at(-1)?.path;
      usedPredefinedStruct = false;
    }

    let slot = CookieUtils.get(CONSTANTS.QUIZIZZ_EXP_SLOT_KEY);

    if (!slot) {
      slot = 0;
    } else {
      slot = Number(slot);
    }

    const data = {
      eventName: CONSTANTS.PAGEVIEW3,
      location: {
        country: pageView3Params.requestCountry ? pageView3Params.requestCountry : null,
        region: pageView3Params.region ? pageView3Params.region : null,
      },
      userId: pageView3Params.userId,
      language: pageView3Params.locale ? pageView3Params.locale : null,
      deviceId: CookieUtils.get('quizizz_uid') || null,
      sessionId: this.sessionId,
      slot,
      experiment: CookieUtils.get(CONSTANTS.QUIZIZZ_EXP_NAME_KEY) || '',
      referrer: pageView3Params.referrer,
      screenType: pageView3Params.screenType ? pageView3Params.screenType : null,
      platform: this.platform,
      featureFlags: pageView3Params.featureFlags ?? this.getFeatureFlags(),
      deviceTime: Date.now(),
      timezoneOffset: new Date().getTimezoneOffset(),
      startingReferrer: this.startingReferrer,

      utmParams: {
        ...QAnalyticsClient.getUtmParams(),
      },
      url: {
        url: currentUrl?.path,
        urlStruct,
        queryParams: currentUrl?.fullPath,
      },
    };

    if (data.url.url.includes('quiz/start_new') && data.url.urlStruct === '/admin/:quizType/:id/:slug?') {
      this.logEvent('url_struct_mismatch', {
        params: {
          to: currentUrl.fullPath,
          from: previousUrl.fullPath,
          prevUrlStruct: previousUrl.matched.at(-1)?.path,
          currentUrlStruct: urlStruct,
          usedPredefinedStruct,
          refSource: data.referrer.sourceName,
        },
      });
    }

    return data;
  }

  logToAnalyticsV2(data: Record<string, any>, type: 'pageview3' | 'frontend' | 'other') {
    const body = JSON.stringify(data);
    let overrideUrl = getEventTrackingUrlV2(QAnalyticsClient.isProd);

    if (type === 'pageview3') {
      overrideUrl = `${overrideUrl}/pv3`;
    }
    if (type === 'frontend') {
      overrideUrl = `${overrideUrl}/frontend`;
    }
    if (type === 'other') {
      overrideUrl = `${overrideUrl}/other2`;
    }

    if (navigator && typeof navigator.sendBeacon !== 'undefined') {
      if (!navigator.sendBeacon(overrideUrl, body)) {
        QAnalyticsClient.createError(`navigator.sendBeacon could not queue the request with data \n ${JSON.parse(body)}`);
      }
    } else {
      fetch(overrideUrl, {
        method: 'POST',
        cache: 'no-cache',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body,
        keepalive: true,
      }).catch((e) => {
        if (!QAnalyticsClient.isProd) {
          // eslint-disable-next-line no-console
          console.error(e);
        }
      });
    }
  }

  logPageView3Event(currentUrl: RouteLocation, previousUrl: RouteLocation, pageView3Params: PageView3Params): void {
    IMStorage.setItem('pv3CurrentUrl', currentUrl || null);
    IMStorage.setItem('pv3PreviousUrl', previousUrl || null);
    const eventData = this.createPageView3Event(currentUrl, previousUrl, pageView3Params);
    this.send([eventData]);
    if (eventData.featureFlags['analytics-v2'] === 'ENABLED') {
      this.logToAnalyticsV2(eventData, 'pageview3');
    }
  }
}
